import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import "./InvoiceUploadModal.css";

const ACCEPTED_TYPES = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
};

const InvoiceUploadModal = ({ onClose }) => {
    const { currentLicense } = useLicense();
    const { currentLocation } = useLocation_();
    const toast = useToast();
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const [invoiceId, setInvoiceId] = useState(null);
    const pollRef = useRef(null);

    const onDrop = useCallback((accepted, rejected) => {
        if (rejected.length) {
            toast.warning("Only PDF, JPEG, and PNG files are accepted (max 15MB).");
        }
        if (accepted.length > 0) {
            setFile(accepted[0]);
            setError(null);
        }
    }, [toast]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPTED_TYPES,
        maxSize: 15 * 1024 * 1024,
        multiple: false,
        disabled: status !== "idle",
    });

    useEffect(() => {
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, []);

    const pollJob = async (jobId) => {
        try {
            const res = await apiClient.get(`/invoices/job/${jobId}`);
            const { status: jobStatus, invoiceId: id, errorMessage } = res.data;

            if (jobStatus === "review") {
                setStatus("ready");
                setInvoiceId(id);
                return;
            }
            if (jobStatus === "failed") {
                setStatus("error");
                setError(errorMessage || "Processing failed");
                return;
            }
            pollRef.current = setTimeout(() => pollJob(jobId), 3000);
        } catch {
            setStatus("error");
            setError("Failed to check processing status");
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setStatus("uploading");
        setError(null);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const res = await apiClient.post("/invoices/upload", formData, {
                headers: {
                    licenseno: currentLicense.licenseNo,
                    locationid: currentLocation?.locationId || "",
                },
            });

            const { jobId } = res.data;
            if (!jobId) throw new Error("No jobId returned");

            setStatus("processing");
            pollJob(jobId);
        } catch (err) {
            setStatus("error");
            setError(err.response?.data?.error || err.message || "Upload failed");
        }
    };

    const handleGoToReview = () => {
        onClose();
        navigate(`/invoice-review/${invoiceId}`);
    };

    const handleRetry = () => {
        setStatus("idle");
        setError(null);
        setFile(null);
    };

    const fileTypeBadge = (f) => {
        if (!f) return null;
        const ext = f.name.split(".").pop().toUpperCase();
        return <span className="inv-upload__file-type">{ext}</span>;
    };

    return (
        <div className="inv-upload-backdrop" onClick={status === "idle" ? onClose : undefined}>
            <div className="inv-upload" onClick={(e) => e.stopPropagation()}>
                <div className="inv-upload__header">
                    <h3 className="inv-upload__title">Upload Invoice</h3>
                    <button className="inv-upload__close" onClick={onClose} disabled={status === "uploading" || status === "processing"} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="inv-upload__body">
                    {status === "idle" && (
                        <>
                            <div
                                {...getRootProps()}
                                className={`inv-upload__dropzone${isDragActive ? " inv-upload__dropzone--active" : ""}`}
                            >
                                <input {...getInputProps()} />
                                <svg className="inv-upload__dropzone-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="12" y1="18" x2="12" y2="12"/>
                                    <polyline points="9 15 12 12 15 15"/>
                                </svg>
                                <p className="inv-upload__dropzone-primary">
                                    {isDragActive ? "Drop invoice here" : "Drop invoice here or click to browse"}
                                </p>
                                <p className="inv-upload__dropzone-secondary">PDF, JPEG, or PNG (max 15MB)</p>
                            </div>

                            {file && (
                                <div className="inv-upload__file-info">
                                    <div className="inv-upload__file-name">
                                        {fileTypeBadge(file)}
                                        {file.name}
                                    </div>
                                    <button className="inv-upload__file-remove" onClick={() => setFile(null)} aria-label="Remove file">
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {(status === "uploading" || status === "processing") && (
                        <div className="inv-upload__status">
                            <div className="inv-upload__spinner" />
                            <p className="inv-upload__status-text">
                                {status === "uploading" ? "Uploading invoice..." : "Extracting data from invoice..."}
                            </p>
                            <p className="inv-upload__status-sub">This may take a minute</p>
                        </div>
                    )}

                    {status === "ready" && (
                        <div className="inv-upload__status">
                            <svg className="inv-upload__check" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <p className="inv-upload__status-text">Invoice processed successfully</p>
                            <p className="inv-upload__status-sub">Review the extracted data and confirm to create products</p>
                        </div>
                    )}

                    {status === "error" && (
                        <div className="inv-upload__status">
                            <svg className="inv-upload__error-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <p className="inv-upload__status-text inv-upload__status-text--error">{error}</p>
                        </div>
                    )}
                </div>

                <div className="inv-upload__footer">
                    {status === "idle" && (
                        <>
                            <button className="inv-upload__btn inv-upload__btn--cancel" onClick={onClose}>Cancel</button>
                            <button className="inv-upload__btn inv-upload__btn--submit" onClick={handleUpload} disabled={!file}>
                                Upload & Process
                            </button>
                        </>
                    )}
                    {status === "ready" && (
                        <button className="inv-upload__btn inv-upload__btn--submit" onClick={handleGoToReview}>
                            Review Extracted Data
                        </button>
                    )}
                    {status === "error" && (
                        <>
                            <button className="inv-upload__btn inv-upload__btn--cancel" onClick={onClose}>Close</button>
                            <button className="inv-upload__btn inv-upload__btn--submit" onClick={handleRetry}>Try Again</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoiceUploadModal;
